import React, { useEffect, useMemo, useState } from 'react';
import { get, round } from 'lodash';
import Modal from '../../../design/shared/modal';
import {
  ModalFieldLabel,
  ModalFooter,
  Table,
  Td,
  TdImp,
  TdLabel,
  Th,
  Tr,
  customStyles,
  Button
} from './css';
import { Formik, Form, Field } from 'formik';
import { Delete, Edit, IconButton } from '@tds/core-interactive-icon';
import request from '../../../utils/request';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { getLocale } from '../../../misc';


const RedeemOffer = ({
  onChange,
  defaultValueRO,
  orderData,
  setDueMonthly,
  setDueNow,
  redeemOfferData,
  setRedeemOfferData,
  setIsFormValidated
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState();
  const [updateData, setUpdateData] = useState();
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [error, setError] = useState(null);

  const locale = getLocale();

  const {
    WLS: { _couponCode, _validationCode, _discountAmount, _duration, 
      _redeemOffer, _validateCode, _add, _couponCodeInvalid, _couponCodeExpired
    } 
  } = locale === 'en-US' ? EN : FR;


  const onCloseModal = () => {
    setError(null);
    setModalVisible(false);
    setIsButtonVisible(true);
  };

  const showModal = () => {
    setUpdateData(null);
    setModalVisible(true);
  };

  const addInFooter = modalData => {
    if (modalData.duration === 'One-Time') {
      setDueNow(value => value + modalData.promotionAmount);
    } else {
      setDueMonthly(value => value + modalData.promotionAmount);
    }
  };

  const removeInFooter = modalData => {
    if (modalData.duration === 'One-Time') {
      setDueNow(value => value - modalData.promotionAmount);
    } else {
      setDueMonthly(value => value - modalData.promotionAmount);
    }
  };

  const addData = () => {
    setIsFormValidated(false);
    if (updateData) {
      // Update existing row
      const updatedSelections = [...redeemOfferData];
      const updatePrice =
        updatedSelections[redeemOfferData.indexOf(updateData)];
        addInFooter(updatePrice);
      updatedSelections[redeemOfferData.indexOf(updateData)] = modalData;
      setRedeemOfferData(updatedSelections);
    } else {
      // Add new row
      setRedeemOfferData([...redeemOfferData, modalData]);
    }
    removeInFooter(modalData);
    setIsButtonVisible(true);
    setModalVisible(false);
  };

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setIsFormValidated(false);
    addInFooter(redeemOfferData[idx]);
    setRedeemOfferData([
      ...redeemOfferData.slice(0, idx),
      ...redeemOfferData.slice(idx + 1)
    ]);
  };

  const editItem = row => {
    setError(null);
    setUpdateData(row);
    setModalVisible(true);
  };

  const handleFieldChange = (values, setFieldValue, fieldName, fieldValue) => {
    if (fieldName !== 'promotionAmount' || fieldName !== 'duration') {
      setFieldValue('promotionAmount', '');
      setFieldValue('duration', '');
    }
    setIsButtonVisible(true);
    setError(null);
    setFieldValue(fieldName, fieldValue);
  };

  const totals = useMemo(() => {
    let totalAmount = 0;
    redeemOfferData.forEach(item => {
      totalAmount += round(item.promotionAmount, 2);
    });
    return {
      totalCost: totalAmount
    };
  }, [redeemOfferData]);

  const redeemOfferAdjustedTotalPrice = useMemo(() => {
    let monthlyPrice = 0;
    let oneTimePrice = 0;
    redeemOfferData.forEach(item => {
      if (item.duration === 'One-Time') {
        oneTimePrice += round(item.promotionAmount, 2);
      } else {
        monthlyPrice += round(item.promotionAmount, 2);
      }
    });
    return {
      monthlyPrice: monthlyPrice,
      oneTimePrice: oneTimePrice
    };
  }, [redeemOfferData]);

  useEffect(() => {
    onChange({
      redeemOfferSelection: redeemOfferData,
      redeemOfferAdjustedTotalPrice
    });
  }, [redeemOfferData, redeemOfferAdjustedTotalPrice]);

  useEffect(() => {
    setRedeemOfferData(get(defaultValueRO, 'redeemOfferSelection', []));
  }, [orderData]);

  const handleWheel = e => e.target.blur();

  return (
    <div>
      {!!redeemOfferData.length && (
        <Table>
          <Th>{_couponCode}</Th>
          <Th>{_validationCode}</Th>
          <Th>{_discountAmount}</Th>
          <Th>{_duration}</Th>
          <Th colSpan={2}>Actions</Th>{' '}
          {/* Add this column for edit/delete buttons */}
          {redeemOfferData.map(
            (
              { couponCode, validationCode, promotionAmount, duration },
              idx
            ) => (
              <Tr key={`${couponCode}-${validationCode}-${idx}`}>
                <Td>{couponCode}</Td>
                <Td>{validationCode}</Td>
                <Td>{promotionAmount}</Td>
                <Td>{duration}</Td>
                <Td>
                  <IconButton
                    icon={Edit}
                    onClick={() => editItem(redeemOfferData[idx])}
                  />
                </Td>
                <Td>
                  <IconButton icon={Delete} onClick={e => deleteItem(e, idx)} />
                </Td>
              </Tr>
            )
          )}
          <Tr>
            <TdLabel colSpan={2}>Total Promotion Amount</TdLabel>
            <TdImp>{totals.totalCost}</TdImp>
            <Td />
            <Td />
          </Tr>
        </Table>
      )}
      <Button>
        <button onClick={showModal} className="frg-button color-green">
          {_redeemOffer}
        </button>
      </Button>
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        customStyles={customStyles}
        header={updateData ? 'Edit Redeem Offer' : _redeemOffer}
      >
        <Formik
          initialValues={
            updateData || {
              couponCode: '',
              validationCode: '',
              promotionAmount: '',
              duration: ''
            }
          }
          onSubmit={(values, { setFieldValue }) => {
            const exportValues = `/coupons/check-coupon`;
            request
              .post(exportValues, { ...values })
              .then(response => {
                const {
                  isValid,
                  isActive,
                  duration,
                  amount,
                  couponCode,
                  validationCode
                } = response.data;
                if (isValid && isActive) {
                  setFieldValue('promotionAmount', amount);
                  setFieldValue('duration', duration);
                  setModalData({
                    couponCode: couponCode,
                    validationCode: validationCode,
                    promotionAmount: amount,
                    duration: duration
                  });
                  setIsButtonVisible(false);
                }
                if (!isValid) {
                  setError(_couponCodeInvalid);
                } else if (isValid && !isActive) {
                  setError(_couponCodeExpired);
                }
              })
              .catch();
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              {error && (
                <div style={{ color: 'red', fontSize: '20px' }}>{error}</div>
              )}
              <div>
                <ModalFieldLabel htmlFor="couponCode">
                  {_couponCode}
                </ModalFieldLabel>
                <Field
                  type="text"
                  id="couponCode"
                  name="couponCode"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e =>
                    handleFieldChange(
                      values,
                      setFieldValue,
                      'couponCode',
                      e.target.value
                    )
                  }
                  required
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="validationCode">
                  {_validationCode}
                </ModalFieldLabel>
                <Field
                  type="text"
                  id="validationCode"
                  name="validationCode"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e =>
                    handleFieldChange(
                      values,
                      setFieldValue,
                      'validationCode',
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="promotionAmount">
                  {_discountAmount}
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="promotionAmount"
                  name="promotionAmount"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="cost">{_duration}</ModalFieldLabel>
                <Field
                  type="text"
                  id="duration"
                  name="duration"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>
              <ModalFooter>
                <Button>
                  <button
                    type="submit"
                    disabled={!isButtonVisible}
                    className="frg-button color-green"
                  >
                    <span> {_validateCode} </span>
                  </button>
                </Button>
                <Button>
                  <button
                    className="frg-button color-green"
                    disabled={isButtonVisible}
                    onClick={addData}
                  >
                    <span>{_add}</span>
                  </button>
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default RedeemOffer;
